import { useEffect, useMemo, useState } from 'react'
import type { AccountPlan, AppStatus, CurrentAccount } from './types'
import { api } from './lib/api'
import { parseAccountMailLocation, type AccountMailLocation } from './lib/accountRoute'
import { parsePracticeLocation, songPracticePath } from './lib/practiceRoute'
import { isRecordingStudioPath, RECORDING_STUDIO_PATH } from './lib/recordingStudioRoute'
import { applyScale, applyTheme, bindScaleToViewport, currentScale, currentTheme } from './lib/theme'
import { ACCOUNT_PANEL_STATE_VERSION, AccountPanel } from './components/AccountPanel'
import { AuthPanel } from './components/AuthPanel'
import { AuthoringWorkspace } from './components/AuthoringWorkspace'
import { RecordingStudio } from './components/RecordingStudio'
import { SegnoVault } from './components/SegnoVault'
import { AlbumPractice } from './components/AlbumPractice'
import { PracticePlayer } from './components/PracticePlayer'
import { ShareUnavailableNotice } from './components/ShareUnavailableNotice'
import { RecoveryEmailVerificationPanel } from './components/RecoveryEmailVerificationPanel'
import { SettingsPanel } from './components/SettingsPanel'
import { AdminPanel } from './components/AdminPanel'
import { isAdminPath } from './lib/adminRoute'
import { useI18n } from './i18n/useI18n'
import { legalDocumentUrl } from './lib/legalLocale'

type View = 'home' | 'author' | 'recording' | 'vault' | 'account' | 'settings'

const FRONTEND_RUNTIME_VERSION = '0.15.15'
const FRONTEND_RUNTIME_STORAGE_KEY = 'miosegno-frontend-runtime-version'


async function configureServiceWorkerForCurrentMode() {
  if (!('serviceWorker' in navigator)) return

  // Vite 개발 서버에서는 이전 소스 모듈을 서비스워커가 캐시하면
  // 새 로그인 화면을 배포해도 브라우저가 오래된 코드를 계속 실행할 수 있다.
  // 개발 모드에서는 기존 등록과 mioSegno 캐시를 자동으로 제거한다.
  if (import.meta.env.DEV) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((registration) => registration.unregister()))

    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(
        keys
          .filter((key) => key.startsWith('miosegno-'))
          .map((key) => caches.delete(key)),
      )
    }
    return
  }

  await navigator.serviceWorker.register('/sw.js')
}

export default function App() {
  const { locale, t } = useI18n()
  const adminRoute = useMemo(() => isAdminPath(location.pathname), [])
  const initialPracticeLocation = useMemo(
    () => parsePracticeLocation(location.pathname, location.search),
    [],
  )
  const queryAlbumToken = initialPracticeLocation.albumToken
  const queryAlbumSong = initialPracticeLocation.albumSong
  const queryShareId = initialPracticeLocation.shareId
  const queryOwnerSongId = initialPracticeLocation.ownerSongId
  const queryNoticeCode = initialPracticeLocation.noticeCode
  const initialRecordingStudioRoute = useMemo(() => isRecordingStudioPath(location.pathname), [])

  // 비밀번호 재설정과 복구 이메일 인증 메일 링크를 최상위에서 해석합니다.
  // 계정 메일 링크는 로그인 여부와 무관하게 홈보다 우선 표시해야 합니다.
  const initialAccountMailRoute = useMemo(
    () => parseAccountMailLocation(location.pathname, location.search),
    [],
  )

  const [shareId, setShareId] = useState(queryShareId)
  const [ownerSongId, setOwnerSongId] = useState<number | null>(queryOwnerSongId)
  const [albumToken, setAlbumToken] = useState(queryAlbumToken)
  const [noticeCode, setNoticeCode] = useState<string | null>(queryNoticeCode)
  const [accountMailRoute, setAccountMailRoute] = useState<AccountMailLocation | null>(
    initialAccountMailRoute.openAccountView ? initialAccountMailRoute : null,
  )
  const [view, setView] = useState<View>(
    initialAccountMailRoute.openAccountView ? 'account' : initialRecordingStudioRoute ? 'recording' : 'home',
  )
  const [account, setAccount] = useState<CurrentAccount | null>(null)
  const [plan, setPlan] = useState<AccountPlan | null>(null)
  const [appStatus, setAppStatus] = useState<AppStatus | null>(null)
  const [accountNotice, setAccountNotice] = useState('')
  const [editingSongId, setEditingSongId] = useState<number | null>(null)
  const [authoringDirty, setAuthoringDirty] = useState(false)
  const [recordingStudioDirty, setRecordingStudioDirty] = useState(false)

  useEffect(() => {
    // 개발 중 프런트 소스가 바뀌어도 React Fast Refresh가 이전 컴포넌트 상태를
    // 보존할 수 있습니다. 버전이 바뀐 첫 실행에는 한 번만 전체 새로고침하여
    // 이전 탭 메시지 상태가 새 코드에 남지 않도록 합니다. 토큰이나 개인정보는
    // 저장하지 않고 UI 버전 문자열만 sessionStorage에 기록합니다.
    const previousRuntimeVersion = sessionStorage.getItem(FRONTEND_RUNTIME_STORAGE_KEY)
    if (previousRuntimeVersion !== FRONTEND_RUNTIME_VERSION) {
      sessionStorage.setItem(FRONTEND_RUNTIME_STORAGE_KEY, FRONTEND_RUNTIME_VERSION)
      location.reload()
      return
    }

    applyTheme(currentTheme())
    applyScale(currentScale())
    const unbindScale = bindScaleToViewport()
    void configureServiceWorkerForCurrentMode()
    void refreshStatus()
    void refreshAccount()
    return unbindScale
  }, [])

  async function refreshStatus() {
    try {
      setAppStatus(await api.status())
    } catch {
      setAppStatus(null)
    }
  }

  async function refreshAccount() {
    try {
      const nextAccount = await api.me()
      setAccount(nextAccount)
      if (isRecordingStudioPath(location.pathname) && nextAccount.termsReconsentRequired) {
        setAccountNotice(t('shell.recording.requiresLegal'))
        setView('account')
      }
      try {
        setPlan(await api.accountPlan())
      } catch {
        setPlan(null)
      }
    } catch {
      setAccount(null)
      setPlan(null)
      if (isRecordingStudioPath(location.pathname)) {
        setAccountNotice(t('shell.recording.requiresLogin'))
        setView('account')
      }
    }
  }

  async function refreshPlan() {
    try {
      setPlan(await api.accountPlan())
    } catch {
      setPlan(null)
    }
  }

  async function logout() {
    await api.logout()
    setAccountNotice('')
    setAccount(null)
    setPlan(null)
    selectView('home')
  }

  /** 계정 메일 화면을 떠날 때 주소창의 일회용 토큰을 즉시 제거합니다. */
  function selectView(nextView: View) {
    const leavingAuthorWithChanges = view === 'author' && nextView !== 'author' && authoringDirty
    const leavingRecordingWithChanges = view === 'recording' && nextView !== 'recording' && recordingStudioDirty
    if (leavingAuthorWithChanges || leavingRecordingWithChanges) {
      const leave = window.confirm(t('shell.unsaved.confirm'))
      if (!leave) return false
      if (leavingAuthorWithChanges) setAuthoringDirty(false)
      if (leavingRecordingWithChanges) setRecordingStudioDirty(false)
    }
    if (accountMailRoute && nextView !== 'account') {
      history.replaceState({}, '', '/')
      setAccountMailRoute(null)
    }
    if (nextView === 'recording') {
      if (!isRecordingStudioPath(location.pathname)) history.pushState({}, '', RECORDING_STUDIO_PATH)
    } else if (isRecordingStudioPath(location.pathname)) {
      history.pushState({}, '', '/')
    }
    setView(nextView)
    return true
  }

  function openAuthor() {
    if (account && !account.termsReconsentRequired) {
      setEditingSongId(null)
      selectView('author')
      return
    }
    setAccountNotice(account?.termsReconsentRequired
      ? t('shell.author.requiresLegal')
      : t('shell.author.requiresLogin'))
    selectView('account')
  }

  function openRecordingStudio() {
    if (account && !account.termsReconsentRequired) {
      selectView('recording')
      return
    }
    if (!isRecordingStudioPath(location.pathname)) history.pushState({}, '', RECORDING_STUDIO_PATH)
    setAccountNotice(account?.termsReconsentRequired
      ? t('shell.recording.requiresLegal')
      : t('shell.recording.requiresLogin'))
    setView('account')
  }

  function openVault() {
    if (account && !account.termsReconsentRequired) {
      selectView('vault')
      return
    }
    setAccountNotice(account?.termsReconsentRequired
      ? t('shell.vault.requiresLegal')
      : t('shell.vault.requiresLogin'))
    selectView('account')
  }

  function editStoredSong(songId: number) {
    setEditingSongId(songId)
    selectView('author')
  }

  function openShare(id: string) {
    const normalized = id.trim().toUpperCase()
    if (!normalized) return
    history.pushState({}, '', songPracticePath(normalized))
    setOwnerSongId(null)
    setNoticeCode(null)
    setShareId(normalized)
  }

  function goHome() {
    history.pushState({}, '', '/')
    setShareId(null)
    setOwnerSongId(null)
    setAlbumToken(null)
    setNoticeCode(null)
    setView('home')
  }

  if (adminRoute) {
    return <AdminPanel />
  }

  if (noticeCode) {
    return <ShareUnavailableNotice code={noticeCode} onHome={goHome} />
  }

  if (albumToken) {
    return <AlbumPractice albumToken={albumToken} initialShareId={queryAlbumSong} onBack={goHome} />
  }

  if (ownerSongId != null) {
    return <PracticePlayer ownerSongId={ownerSongId} onBack={goHome} />
  }

  if (shareId) {
    return <PracticePlayer shareId={shareId} onBack={goHome} />
  }

  const accountLabel = account?.displayName || account?.username || t('nav.login')
  const authenticated = account !== null && !account.termsReconsentRequired

  return (
    <main className="page app-shell">
      <header className="topbar">
        <div className="brand-plan-cluster">
          <button className="brand-button" onClick={() => selectView('home')} aria-label={t('nav.homeAria')}>
            <img className="brand-app-icon" src="/brand-icon.png" alt="" />
            <span><strong>mioSegno</strong><small>by 2n4u</small></span>
          </button>
          {plan && (
            <button className="header-plan-button" type="button" onClick={() => selectView('account')} title={t('nav.currentPlanTitle')}>
              {t(`account.plan.name.${plan.code}`, {}, plan.code)}
            </button>
          )}
        </div>
        <nav className="topnav" aria-label={t('nav.mainAria')}>
          <button className={view === 'home' ? 'nav active' : 'nav'} onClick={() => selectView('home')}>{t('nav.home')}</button>
          <button className={view === 'author' ? 'nav active' : 'nav'} onClick={openAuthor}>{t('nav.author')}</button>
          <button className={view === 'recording' ? 'nav active' : 'nav'} onClick={openRecordingStudio}>{t('nav.recordingStudio')}</button>
          <button className={view === 'vault' ? 'nav active' : 'nav'} onClick={openVault}>{t('nav.vault')}</button>
          <button className={view === 'account' ? 'nav active' : 'nav'} onClick={() => selectView('account')}>{accountLabel}</button>
          <button
            className={view === 'settings' ? 'nav nav-settings active' : 'nav nav-settings'}
            type="button"
            onClick={() => selectView('settings')}
            aria-label={t('nav.settings')}
            title={t('nav.settings')}
          >
            {/* 언어를 아직 읽지 못하는 사용자도 설정 진입점을 알아볼 수 있도록 텍스트 대신 공용 톱니 아이콘을 표시합니다. */}
            <svg className="nav-settings-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M12 8.75a3.25 3.25 0 1 0 0 6.5 3.25 3.25 0 0 0 0-6.5Zm8.1 3.25c0-.42-.04-.83-.1-1.23l2.02-1.58-2-3.46-2.38.96a8.57 8.57 0 0 0-2.13-1.23L15.15 3h-4.3l-.36 2.46a8.57 8.57 0 0 0-2.13 1.23l-2.38-.96-2 3.46L6 10.77A8.4 8.4 0 0 0 5.9 12c0 .42.04.83.1 1.23l-2.02 1.58 2 3.46 2.38-.96c.65.5 1.37.92 2.13 1.23l.36 2.46h4.3l.36-2.46a8.57 8.57 0 0 0 2.13-1.23l2.38.96 2-3.46L20 13.23c.06-.4.1-.81.1-1.23Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </nav>
      </header>

      {import.meta.env.DEV && appStatus?.demoMode && (
        <section className="integration-banner" role="status">
          <strong>{t('dev.localMode.title')}</strong>
          <span>{t('dev.localMode.description')}</span>
        </section>
      )}
      {view === 'home' && (
        <Home
          openShare={openShare}
          authenticated={authenticated}
          onAuthor={openAuthor}
          status={appStatus}
        />
      )}
      {view === 'author' && authenticated && (
        <AuthoringWorkspace
          appStatus={appStatus}
          plan={plan}
          initialSongId={editingSongId}
          onEditLoaded={() => setEditingSongId(null)}
          onUnsavedChangesChange={setAuthoringDirty}
          onCreatedPractice={(songId) => {
            setAuthoringDirty(false)
            setOwnerSongId(songId)
          }}
        />
      )}
      {view === 'recording' && authenticated && (
        <RecordingStudio ownerId={account.userId} onUnsavedChangesChange={setRecordingStudioDirty} />
      )}
      {view === 'vault' && authenticated && (
        <SegnoVault
          appStatus={appStatus}
          plan={plan}
          onEditSong={editStoredSong}
          onCreateNew={openAuthor}
        />
      )}
      {view === 'account' && (
        <section className="account-page">
          {accountMailRoute?.action === 'recovery-email-verify'
            ? (
                <RecoveryEmailVerificationPanel
                  token={accountMailRoute.token}
                  userId={accountMailRoute.recoveryEmailUserId}
                  onClosed={() => setAccountMailRoute(null)}
                />
              )
            : accountMailRoute?.action === 'password-reset'
              ? (
                  <AuthPanel
                    appStatus={appStatus}
                    demoMode={appStatus?.demoMode}
                    onSuccess={(nextAccount) => {
                      setAccount(nextAccount)
                      void refreshPlan()
                      selectView('home')
                    }}
                    onRecoveryRouteClosed={() => setAccountMailRoute(null)}
                  />
                )
              : account
                ? (
                    <AccountPanel
                      key={ACCOUNT_PANEL_STATE_VERSION}
                      account={account}
                      status={appStatus}
                      plan={plan}
                      onLogout={logout}
                      onAccountChanged={setAccount}
                      onPasswordChanged={(message) => {
                        setAccountNotice(message)
                        setAccount(null)
                        setPlan(null)
                        setView('account')
                      }}
                    />
                  )
                : (
                    <AuthPanel
                      appStatus={appStatus}
                      demoMode={appStatus?.demoMode}
                      initialMessage={accountNotice || undefined}
                      onSuccess={(nextAccount) => {
                        setAccountNotice('')
                        setAccount(nextAccount)
                        void refreshPlan()
                        if (isRecordingStudioPath(location.pathname) && !nextAccount.termsReconsentRequired) {
                        setView('recording')
                      } else {
                        selectView('home')
                      }
                      }}
                    />
                  )}
        </section>
      )}
      {view === 'settings' && <SettingsPanel appStatus={appStatus} />}

      <footer className="site-footer">
        <span>{t('footer.brand')}</span>
        <span className="site-footer-policy-links">
          {appStatus?.serviceTermsUrl && <a href={legalDocumentUrl(appStatus.serviceTermsUrl, locale)} target="_blank" rel="noreferrer">{t('legal.serviceTerms')}</a>}
          {appStatus?.privacyPolicyUrl && <a href={legalDocumentUrl(appStatus.privacyPolicyUrl, locale)} target="_blank" rel="noreferrer">{t('legal.privacy')}</a>}
          {appStatus?.pricingPolicyUrl && <a href={appStatus.pricingPolicyUrl} target="_blank" rel="noreferrer">{t('legal.pricing')}</a>}
          {appStatus?.copyrightPolicyUrl && <a href={appStatus.copyrightPolicyUrl} target="_blank" rel="noreferrer">{t('legal.copyright')}</a>}
        </span>
      </footer>
    </main>
  )
}

function Home({
  openShare,
  authenticated,
  onAuthor,
  status,
}: {
  openShare: (id: string) => void
  authenticated: boolean
  onAuthor: () => void
  status: AppStatus | null
}) {
  const { t } = useI18n()
  const [id, setId] = useState('')
  return (
    <>
      <section className="hero card">
        <div>
          <p className="eyebrow">{t('home.hero.eyebrow')}</p>
          <h1>{t('home.hero.titleLine1')}<br />{t('home.hero.titleLine2')}</h1>
          <p>{t('home.hero.description')}</p>
          <div className="hero-actions">
            <button className="button primary large" onClick={onAuthor}>{authenticated ? t('home.hero.createMine') : t('home.hero.loginCreate')}</button>
          </div>
          {import.meta.env.DEV && status?.demoMode && <p className="demo-hint">{t('dev.demo.prefix')} <strong>demo-leader / demo-password</strong>{t('dev.demo.suffix')}</p>}
        </div>
        <div className="segno-visual" aria-hidden="true"><span>𝄋</span><small>{t('home.visual.repeat')}</small></div>
      </section>

      <section className="share-entry card">
        <div><p className="eyebrow">{t('home.share.eyebrow')}</p><h2>{t('home.share.title')}</h2></div>
        <form onSubmit={(event) => { event.preventDefault(); openShare(id) }}>
          <input value={id} onChange={(event) => setId(event.target.value)} placeholder={t('home.share.placeholder')} />
          <button className="button primary" type="submit">{t('common.open')}</button>
        </form>
      </section>

      <section className="feature-grid">
        <article className="card"><span className="feature-icon">A↔B</span><h3>{t('home.feature.fine.title')}</h3><p>{t('home.feature.fine.description')}</p></article>
        <article className="card"><span className="feature-icon">0.4s</span><h3>{t('home.feature.cue.title')}</h3><p>{t('home.feature.cue.description')}</p></article>
        <article className="card"><span className="feature-icon">PDF</span><h3>{t('home.feature.pdf.title')}</h3><p>{t('home.feature.pdf.description')}</p></article>
      </section>
    </>
  )
}

function IntroCard({ status }: { status: AppStatus | null }) {
  const { t } = useI18n()
  return (
    <article className="card">
      <p className="eyebrow">{t('nav.author')}</p>
      <h2>{t('intro.loginRequired')}</h2>
      <p>{t('intro.description')}</p>
      <p className="muted">
        {status?.mediaApiEnabled
          ? t('intro.audioEnabled')
          : t('intro.audioPreparing')}
      </p>
    </article>
  )
}
